import { Glue42 } from "../../glue";
import { CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import { LayoutsStream, HibernationResumeMessage, UpdateLayoutComponentContextMessage, LayoutsLibConfiguration, ResetLayoutMessage } from "./types";
import store from "./store";
import generate from "shortid";
import { ACSSaveLayoutMessage, ACSRestoreMessage, ACSRestoreOptionsMessage, ACSMessage, ACSRenameMessage, ACSRemoveLayoutMessage, ACSImportMessage, ExportResultMessage } from "./types";
import { Glue42Core } from "@glue42/core";
import ContextProvider from "./contextProvider";
import transformACSLayout from "./transform";
import LayoutImpl from "./layout";
import { isBoolean, isNullOrWhiteSpace, isObject, isUndefinedOrNull } from "../activity/helpers/util";

const LayoutsCommandMethod = "T42.ACS.Command";

export default class LayoutsAPIImpl implements Glue42.Layouts.API {
    private appManager: Glue42.AppManager.API;
    private provider: ContextProvider;
    private isRegisterMethodForLayoutModified = false;

    constructor(private config: LayoutsLibConfiguration, private stream: LayoutsStream, private callbacks: CallbackRegistry, logger: Glue42.Logger.API) {
        this.appManager = config.appManager;
        this.provider = new ContextProvider(config, config.activityGetter, callbacks, logger);
        stream.subscribe();
    }

    public async setDefaultGlobal(name: string): Promise<void> {
        const methodName = "SelectDefaultLayout";
        await this.invokeMethodCore(methodName, { name });
    }

    public async clearDefaultGlobal(): Promise<void> {
        const methodName = "DeselectDefaultLayout";
        this.invokeMethodCore(methodName);
    }

    public async getDefaultGlobal(): Promise<Glue42.Layouts.Layout | undefined> {
        const methodName = "GetDefaultLayout";
        const result = await this.invokeMethodCore(methodName);
        const layout = result.returned;
        if (!layout) {
            return undefined;
        }
        if (this.isSlimMode()) {
            return layout;
        }
        return this.list().find((l) => l.name === layout.name && l.type === "Global");
    }

    public ready(): Promise<void> {
        if (this.config.mode === "fullWaitSnapshot") {
            return this.stream.gotSnapshot;
        }
        return this.stream.ready;
    }

    public save(layout: Glue42.Layouts.NewLayoutOptions): Promise<Glue42.Layouts.Layout> {
        return new Promise<Glue42.Layouts.Layout>((resolve, reject) => {

            this.verifyNotSlimMode();

            if (isUndefinedOrNull(layout)) {
                return reject(new Error("layout is required"));
            }

            if (isNullOrWhiteSpace(layout.name)) {
                return reject(new Error("layout.name argument is required"));
            }

            if (isNullOrWhiteSpace(layout.type)) {
                layout.type = "Global";
            }

            if (!isNullOrWhiteSpace(layout.activityId)) {
                layout.type = "Activity";
            }

            const layoutObject: ACSSaveLayoutMessage = {
                name: layout.name,
                type: layout.type,
                context: layout.context ?? {},
                metadata: layout.metadata ?? {},
                options: {},
            };

            if (layout.type === "Activity") {
                let actId = layout.activityId;
                if (!actId) {
                    if (!this.appManager.myInstance.inActivity) {
                        return reject(new Error("Current application is not in activity. Cannot save activity layout for it."));
                    }
                    actId = this.appManager.myInstance.activityId;
                }
                layoutObject.activityId = actId;
            } else if (layout.type === "Global") {
                if (Array.isArray(layout.ignoreInstances)) {
                    layoutObject.options.ignoreInstances = layout.ignoreInstances;
                }
                if (Array.isArray(layout.instances)) {
                    layoutObject.options.instances = layout.instances;
                }
            } else {
                return reject(new Error(`layout type ${layout.type} is not supported`));
            }

            this.invokeMethodAndTrack("SaveLayout", layoutObject, resolve, reject);
        });
    }

    public restore(options: Glue42.Layouts.RestoreOptions): Promise<Glue42.Layouts.RestoreResumeResult> {
        return new Promise<Glue42.Layouts.RestoreResumeResult>((resolve, reject) => {
            this.verifyNotSlimMode();
            // TODO add validations...
            if (isUndefinedOrNull(options)) {
                return reject(new Error("options argument is required"));
            }

            if (isNullOrWhiteSpace(options.name)) {
                return reject(new Error("options.name argument is required"));
            }

            if (isNullOrWhiteSpace(options.type)) {
                options.type = "Global";
            }

            if (!isNullOrWhiteSpace(options.activityIdToJoin)) {
                options.type = "Activity";
            }

            if (options.type === "Activity") {
                if (isUndefinedOrNull(options.setActivityContext)) {
                    options.setActivityContext = true;
                }
                if (typeof options.setActivityContext !== "boolean") {
                    return reject(new Error("`setActivityContext` must hold a boolean value."));
                }
                options.activityIdToJoin = options.activityIdToJoin ?? this.appManager.myInstance.activityId;
            }

            // backwards compatibility
            if (!isUndefinedOrNull(options.closeRunningInstance)) {
                options.closeRunningInstances = options.closeRunningInstance;
            }

            if (isUndefinedOrNull(options.closeRunningInstances)) {
                options.closeRunningInstances = true;
            }

            if (!isBoolean(options.closeRunningInstances)) {
                return reject(new Error("`closeRunningInstances` must hold a boolean value."));
            }

            if (isUndefinedOrNull(options.closeMe)) {
                options.closeMe = options.closeRunningInstances;
            }

            if (!isBoolean(options.closeMe)) {
                return reject(new Error("`closeMe` must hold a boolean value."));
            }

            if (!isUndefinedOrNull(options.context) && !isObject(options.context)) {
                return reject(new Error("`context` must hold an object value."));
            }

            if (!isUndefinedOrNull(options.timeout) && typeof options.timeout !== "number") {
                return reject(new Error("`timeout` must hold an number value."));
            }

            options.context = options.context ?? {};

            const restoreOptions: ACSRestoreOptionsMessage = {
                activityToJoin: options.activityIdToJoin,
                setActivityContext: options.setActivityContext,
                ignoreActivityWindowTypes: options.ignoreActivityWindowTypes,
                // not used in GD3
                // restoreActivityOwner: options.restoreActivityOwner,
                // closeOldWindows: options.closeRunningInstance,
                reuseExistingWindows: options.reuseWindows,

                closeRunningInstances: options.closeRunningInstances, // new
                excludeFromClosing: options.closeMe ? [] : [this.appManager.myInstance?.id]
            };

            const arg: ACSRestoreMessage = {
                type: options.type,
                name: options.name,
                context: options.context,
                options: restoreOptions
            };
            if (options.timeout) {
                arg.timeout = options.timeout;
            }

            this.invokeMethodAndTrack("RestoreLayout", arg, resolve, reject, true);
        });
    }

    public reset(options: Glue42.Layouts.ResetLayoutOptions): Promise<Glue42.Layouts.RestoreResumeResult> {
        return new Promise<Glue42.Layouts.RestoreResumeResult>((resolve, reject) => {
            this.verifyNotSlimMode();
            if (typeof options !== "object") {
                return reject(new Error("options argument is required"));
            }
            if (isNullOrWhiteSpace(options.layoutId)) {
                return reject(new Error("options.layoutId argument is required"));
            }

            const msg: ResetLayoutMessage = {
                ...options
            };

            this.invokeMethodAndTrack("ResetLayout", msg, resolve, reject, true);
        });
    }

    public remove(type: string, name: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.verifyNotSlimMode();

            if (!name) {
                return reject(new Error("name argument is required"));
            }

            if (!type) {
                return reject(new Error("type argument is required"));
            }

            const msg: ACSRemoveLayoutMessage = {
                type,
                name,
            };

            this.invokeMethodAndTrack("RemoveLayout", msg, resolve, reject);
        });
    }

    public list(): Glue42.Layouts.Layout[] {
        this.verifyNotSlimMode();
        return store.all;
    }

    public import(layouts: Glue42.Layouts.Layout[], mode?: Glue42.Layouts.ImportMode): Promise<Glue42.Layouts.ImportLayoutResult> {
        return new Promise<Glue42.Layouts.ImportLayoutResult>((resolve, reject) => {
            this.verifyNotSlimMode();
            if (!isUndefinedOrNull(mode)) {
                if (mode !== "merge" && mode !== "replace") {
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    return reject(new Error(`${mode} is not supported - only "merge" and "replace"`));
                }
            }
            if (!Array.isArray(layouts)) {
                return reject(new Error("layouts arguments is not an array"));
            }
            const msg: ACSImportMessage = {
                mode: mode || "replace",
                layouts
            };

            this.invokeMethodAndTrack("ImportLayouts", msg, resolve, reject, true);
        });
    }

    public export(layoutType?: Glue42.Layouts.LayoutType): Promise<Glue42.Layouts.Layout[]> {
        return new Promise<Glue42.Layouts.Layout[]>((resolve, reject) => {
            const handleResult = (result: ExportResultMessage) => {
                let layouts: Glue42.Layouts.Layout[] = this.getObjectValues(result.Layouts).map((t) => new LayoutImpl(transformACSLayout(t)));
                if (layoutType) {
                    layouts = layouts.filter((l) => l.type === layoutType);
                }
                resolve(layouts);
            };

            this.invokeMethodAndTrack("ExportLayouts", {}, handleResult, reject, true);
        });
    }

    public rename(layout: Glue42.Layouts.Layout, newName: string): Promise<Glue42.Layouts.LayoutResult> {
        return new Promise<{ status: string }>((resolve, reject) => {
            this.verifyNotSlimMode();

            if (!layout) {
                return reject(new Error("layout argument is required"));
            }

            if (!layout.name) {
                return reject(new Error("name argument is required"));
            }

            if (!layout.type) {
                return reject(new Error("type argument is required"));
            }

            const msg: ACSRenameMessage = { type: layout.type, oldName: layout.name, newName };

            this.invokeMethodAndTrack("RenameLayout", msg, resolve, reject);
        });
    }

    public updateMetadata(layout: Glue42.Layouts.Layout): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!layout) {
                return reject(new Error("layout argument is required"));
            }

            if (!layout.name) {
                return reject(new Error("name argument is required"));
            }

            if (!layout.type) {
                return reject(new Error("type argument is required"));
            }

            if (!layout.metadata) {
                return reject(new Error("metadata argument is required"));
            }

            const layoutObject: any = {
                name: layout.name,
                type: layout.type,
                metadata: layout.metadata
            };

            this.invokeMethodAndTrack("UpdateMetadata", layoutObject, resolve, reject, true);
        });
    }

    public hibernate(name: string, options?: Glue42.Layouts.HibernationOptions): Promise<Glue42.Layouts.HibernateResult> {
        return new Promise((resolve, reject) => {
            if (!name) {
                return reject(new Error("name cannot be empty"));
            }
            options = options || {};
            const request: HibernationResumeMessage = {
                name,
                type: "Global",
                context: options.context || {},
                metadata: options.metadata || {},
            };
            this.invokeMethodAndTrack("HibernateLayout", request, resolve, reject, true);
        });
    }

    public resume(name: string, context?: any, options?: Glue42.Layouts.ResumeOptions): Promise<Glue42.Layouts.RestoreResumeResult> {
        return new Promise((resolve, reject) => {
            if (!name) {
                return reject(new Error("name cannot be empty"));
            }
            const request: HibernationResumeMessage = {
                name,
                type: "Global",
                context,
                ...options
            };
            this.invokeMethodAndTrack("ResumeLayout", request, resolve, reject, true);
        });
    }

    public async getCurrentLayout(): Promise<Glue42.Layouts.Layout | undefined> {
        const methodName = "GetCurrentLayout";
        const result = await this.invokeMethodCore(methodName, {}, "best", { methodResponseTimeoutMs: 5 * 60 * 1000 });
        let layout = result.returned.layout;
        if (!layout) {
            return undefined;
        }
        if (!this.isSlimMode()) {
            // we have internal if we track layouts (so not slim mode), if not return the object we received from GD
            layout = this.list().find((l) => l.name === layout.name && l.type === layout.type);
        }
        return layout;
    }

    public getRestoredLayoutsInfo(): Promise<Glue42.Layouts.GetRestoredLayoutsInfoResult | undefined> {
        return new Promise((resolve, reject) => {
            const methodName = "GetRestoredLayoutsInfo";
            this.invokeMethodCore(methodName)
                .then((result) => {
                    const restoredLayouts = result.returned;
                    resolve(restoredLayouts);
                })
                .catch(reject);
        });
    }

    public onAdded(callback: (layout: Glue42.Layouts.Layout) => void): UnsubscribeFunction {
        const result = this.callbacks.add("added", callback);

        if (store.all.length > 0) {
            store.all.forEach((layout) => {
                try {
                    callback(layout);
                } catch (err) { /** */ }
            });
        }

        return result;
    }

    public onRemoved(callback: (layout: Glue42.Layouts.Layout) => void) {
        return this.callbacks.add("removed", callback);
    }

    public onChanged(callback: (layout: Glue42.Layouts.Layout) => void) {
        return this.callbacks.add("changed", callback);
    }

    public onRestored(callback: (layout: Glue42.Layouts.Layout) => void) {
        return this.callbacks.add("restored", callback);
    }

    public onRenamed(callback: (layout: Glue42.Layouts.Layout) => void) {
        return this.callbacks.add("renamed", callback);
    }

    public onEvent(callback: (event: any) => void) {
        return this.stream.onEvent(callback);
    }

    public onSaveRequested(callback: (info?: Glue42.Layouts.SaveRequestContext) => Glue42.Layouts.SaveRequestResponse): UnsubscribeFunction {
        return this.provider.onSaveRequested(callback);
    }

    public onLayoutModified(callback: (info: Glue42.Layouts.LayoutModifiedEvent) => void): UnsubscribeFunction {
        if (this.isRegisterMethodForLayoutModified === false) {
            this.isRegisterMethodForLayoutModified = true;
            this.registerMethodForLayoutModified();
        }
        return this.callbacks.add("layout-modified", callback);
    }

    public updateAppContextInCurrent(context: object): Promise<void> {
        return new Promise((resolve, reject) => {
            if (context && typeof context !== "object") {
                return reject(new Error("context must be an object"));
            }
            context = context ?? {};
            const request: UpdateLayoutComponentContextMessage = {
                context
            };
            this.invokeMethodAndTrack("UpdateLayoutComponentContext", request, resolve, reject, true);
        });
    }

    public updateDefaultContext(context: object): Promise<Glue42.Layouts.LayoutResult> {
        return new Promise((resolve, reject) => {
            if (context && typeof context !== "object") {
                return reject(new Error("context must be an object"));
            }
            context = context ?? {};
            const request: UpdateLayoutComponentContextMessage = {
                context
            };
            this.invokeMethodAndTrack("UpdateDefaultContext", request, resolve, reject, true);
        });
    }

    public async get(name: string, type: Glue42.Layouts.LayoutType): Promise<Glue42.Layouts.Layout> {
        const matching = this.list().find((l) => l.name === name && l.type === type);
        if (!matching) {
            throw new Error(`cannot find layout with name=${name} and type=${type}`);
        }
        return matching;
    }

    public async getAll(type: Glue42.Layouts.LayoutType): Promise<Glue42.Layouts.LayoutSummary[]> {
        const matching = this.list().filter((l) => type === l.type);
        return matching;
    }

    public async forceRefresh(): Promise<void> {
        const methodName = "RefreshLayouts";
        await this.invokeMethodCore(methodName);
    }

    private isSlimMode() {
        return this.config.mode === "slim";
    }

    private verifyNotSlimMode() {
        if (this.isSlimMode()) {
            throw Error("Operation not allowed in slim mode. Run in full mode.");
        }
    }

    private async registerMethodForLayoutModified() {
        await this.config.agm.register("T42.ACS.LayoutModified", (args: unknown, caller: Glue42.Interop.Instance) => {
            this.callbacks.execute("layout-modified", args);
        });
    }

    private invokeMethodAndTrack(methodName: string, args: ACSMessage, resolve: (result: any) => void, reject: (err: any) => void, skipStreamEvent?: boolean) {
        let streamEventReceived = skipStreamEvent;
        let agmResult: any;

        const token = generate();
        args.token = token;

        const handleResult = () => {
            if (streamEventReceived && agmResult) {
                resolve(agmResult);
            }
        };

        const methodResponseTimeoutMs = 120 * 1000;

        if (!skipStreamEvent) {
            this.stream.waitFor(token, methodResponseTimeoutMs)
                .then(() => {
                    streamEventReceived = true;
                    handleResult();
                })
                .catch((err: any) => {
                    reject(err);
                });
        }

        const responseHandler = (result: Glue42Core.AGM.InvocationResult<{ status?: string, failed?: string }>) => {
            if (!result.returned) {
                return reject(new Error("No result from method " + methodName));
            }

            if (result.returned.status && (result.returned.status !== "Success" && result.returned.status !== "PartialSuccess")) {
                if (typeof (result.returned) === "string") {
                    return reject(new Error(result.returned));
                } else if (typeof (result.returned) === "object") {
                    if (result.returned.status && result.returned.failed) {
                        return reject(new Error(`${result.returned.status}: ${JSON.stringify(result.returned.failed)}`));
                    } else {
                        return reject(new Error(result.returned as string));
                    }
                }
            }

            agmResult = result.returned;
            handleResult();
        };
        this.invokeMethodCore(methodName, args, "best", { methodResponseTimeoutMs })
            .then(responseHandler)
            .catch((err: any) => reject(err));
    }

    private async invokeMethodCore(methodName: string, args?: object, target?: Glue42Core.Interop.InstanceTarget, options?: Glue42Core.Interop.InvokeOptions) {
        if (this.isCommandMethodPresent()) {
            return await this.config.agm.invoke(LayoutsCommandMethod, { command: methodName, data: args }, target, options);
        } else {
            return await this.config.agm.invoke(`T42.ACS.${methodName}`, args, target, options);
        }
    }

    private getObjectValues(obj: any) {
        if (!obj) {
            return [];
        }

        return Object.keys(obj).map((k) => obj[k]);
    }

    private isCommandMethodPresent(): boolean {
        return this.config.agm.methods().some((method) => method.name === LayoutsCommandMethod);
    }
}
